import { createContext, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AUTH_ME_QUERY_KEY, queryClient, sessionLoss } from "@/lib/queryClient";
import { api, edenFetch } from "@/lib/eden";
import { writeSessionHint } from "@/lib/session-hint";
import { toastApiError } from "@/lib/toast-error";
import { clearPendingBrAction } from "@/site/components/birthday-request/pending-br-action";
import type { User } from "@shared/schema";

interface AuthContextValue {
  /**
   * The FULL-session profile. `null` when logged out — and also `null` for a
   * SOFT session, on purpose: a reminder email-link login (PRD 08 D18 / spec
   * 30) is signed in for the send flow only, so every existing "is there a
   * user?" check keeps treating it as logged-out. Surfaces that genuinely want
   * to acknowledge a soft member (the header's welcome, the route guard's
   * browse-flow allowance) read `softUser` deliberately — the same default-deny
   * shape as the server's `requireAuth({ allowSoft })`.
   */
  user: User | null;
  /** The profile behind a SOFT (email-link) session; `null` otherwise. */
  softUser: User | null;
  /** True while the cookie is a soft session. `user` is `null` whenever this is true. */
  isSoft: boolean;
  isLoading: boolean;
  /** The last `/me` failure that wasn't a plain "not signed in" (e.g. a transient 500). */
  error: Error | null;
  /** Dev machines only; `soft` mints the email-link soft session instead. */
  devLogin: (userId: string, opts?: { soft?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  softUser: null,
  isSoft: false,
  isLoading: true,
  error: null,
  devLogin: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    data: profile,
    isLoading,
    error: meError,
  } = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await api.api.user.auth.me.get();
      if (error) {
        if ((error.status as number) === 401 || (error.status as number) === 404) return null;
        // Anything else is a query error, not "logged out" — React Query keeps the
        // last successful `data`, so an established session survives the blip.
        throw error instanceof Error ? error : new Error("Couldn't load your account");
      }
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });

  // `/user/auth/me` accepts soft sessions (2026-09-16) and flags them; split the
  // one payload into the two views described on `AuthContextValue`.
  const isSoft = !!profile?.is_soft;
  const user = profile && !isSoft ? profile : null;
  const softUser = profile && isSoft ? profile : null;

  // Keep the "had a session" hint in step with every settled `/me` (see session-hint.ts).
  // `undefined` = still loading or a transient error with no cached profile: leave it.
  useEffect(() => {
    if (profile !== undefined) writeSessionHint(profile !== null);
    // A session landing settles any "signed out by a 401" the composer hasn't explained yet.
    if (profile) sessionLoss.pending = false;
  }, [profile]);

  useEffect(() => {
    if (profile) {
      Sentry.setUser({
        id: profile.user_id,
        username: profile.username ?? undefined,
        email: profile.email ?? undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [profile]);

  const devLoginMutation = useMutation({
    mutationFn: async ({ user_id, soft }: { user_id: string; soft?: boolean }) => {
      await edenFetch(api.api.public.auth["dev-login"].post({ user_id, soft }));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await edenFetch(api.api.public.auth.logout.post());
    },
    onSuccess: () => {
      // A stashed Birthday-Request funnel action must not survive into the next login —
      // it could fire against a different account (sessionStorage outlives the redirect).
      clearPendingBrAction();
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      queryClient.clear();
      window.location.href = "/login";
    },
    onError: (e) => toastApiError(e, "Couldn't sign you out. Please try again."),
  });

  const devLogin = async (userId: string, opts: { soft?: boolean } = {}) => {
    await devLoginMutation.mutateAsync({ user_id: userId, soft: opts.soft });
  };

  const logout = async () => {
    // `onError` above already toasts; swallow here so it doesn't also surface as
    // an unhandled rejection in Sentry.
    try {
      await logoutMutation.mutateAsync();
    } catch {
      /* reported by onError */
    }
  };

  return (
    <AuthContext value={{ user, softUser, isSoft, isLoading, error: meError, devLogin, logout }}>
      {children}
    </AuthContext>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
